import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BookOpenCheck,
  Check,
  ChevronRight,
  Clock3,
  Edit3,
  FolderPlus,
  GraduationCap,
  KeyRound,
  Layers3,
  Loader2,
  Plus,
  Save,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const decodeToken = (token) => {
  try {
    const payload = token?.split(".")?.[1];
    if (!payload) return null;
    const normalized = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
};

const splitLines = (value) =>
  String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

const ManageThemes = () => {
  const navigate = useNavigate();
  const { t, language, lang } = useLanguage();
  const activeLanguage = language || lang || "fr";
  const tr = (key, fallback) =>
    t?.ui?.themeManager?.[key] || t?.[key] || fallback;

  const [catalog, setCatalog] = useState({ themes: [] });
  const [selectedThemeId, setSelectedThemeId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleteSecretCode, setDeleteSecretCode] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const [newThemeName, setNewThemeName] = useState("");
  const [renamingTheme, setRenamingTheme] = useState(false);
  const [themeName, setThemeName] = useState("");

  const [subThemePanelOpen, setSubThemePanelOpen] = useState(false);
  const [subThemeForm, setSubThemeForm] = useState({
    subThemeId: "",
    name: "",
    duration: "1",
    competenciesFrText: "",
    competenciesEnText: "",
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    let storedUser = null;
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      storedUser = null;
    }

    const tokenData = decodeToken(token);
    const type = tokenData?.type || storedUser?.type;
    const status = tokenData?.status || storedUser?.status;

    if (type !== "Client" || status !== "Active") {
      navigate("/unauthorized", { replace: true });
    }
  }, [navigate, token]);

  const translateApiError = (code, fallback) => {
    const keyByCode = {
      AUTH_REQUIRED: "authRequired",
      ACTIVE_CLIENT_REQUIRED: "activeClientRequired",
      CLIENT_INACTIVE: "clientInactive",
      THEME_MANAGEMENT_FORBIDDEN: "themeManagementForbidden",
      THEME_MANAGEMENT_ACCESS_CHECK_FAILED: "themeManagementAccessCheckFailed",
      DELETE_SECRET_NOT_CONFIGURED: "deleteSecretNotConfigured",
      INVALID_DELETE_SECRET_CODE: "invalidDeleteSecretCode",
      DELETION_CODE_REQUIRED: "deletionCodeRequired",
      DELETION_CODE_NOT_CONFIGURED: "deletionCodeNotConfigured",
      INVALID_DELETION_CODE: "invalidDeletionCode",
      CATALOG_LOAD_FAILED: "loadError",
      THEME_NAME_REQUIRED: "themeNameRequired",
      THEME_ID_NAME_REQUIRED: "themeNameRequired",
      THEME_EXISTS: "themeExists",
      THEME_NOT_FOUND: "themeNotFound",
      THEME_CREATE_FAILED: "themeCreateFailed",
      THEME_UPDATE_FAILED: "themeUpdateFailed",
      THEME_DELETE_FAILED: "themeDeleteFailed",
      SUBTHEME_NAME_REQUIRED: "subThemeNameRequired",
      SUBTHEME_EXISTS: "subThemeExists",
      SUBTHEME_NOT_FOUND: "subThemeNotFound",
      INVALID_DURATION: "invalidDuration",
      COMPETENCY_TRANSLATIONS_MISMATCH: "competencyTranslationsMismatch",
      SUBTHEME_SAVE_FAILED: "subThemeSaveFailed",
      SUBTHEME_DELETE_FAILED: "subThemeDeleteFailed",
      COMPETENCY_NOT_FOUND: "competencyNotFound",
      COMPETENCY_DELETE_FAILED: "competencyDeleteFailed",
      SERVER_ERROR: "serverError",
    };
    const key = keyByCode[code];
    return key ? tr(key, fallback) : fallback;
  };

  const request = async (path, options = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const fallbackMessage =
        data.message || tr("requestFailed", "Opération impossible.");
      const error = new Error(translateApiError(data.code, fallbackMessage));
      error.status = response.status;
      error.code = data.code;
      throw error;
    }
    return data;
  };

  const loadCatalog = async ({ keepSelection = true } = {}) => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const data = await request("/api/certifs/theme-catalog");
      const nextCatalog = {
        ...data,
        themes: Array.isArray(data?.themes) ? data.themes : [],
      };
      setCatalog(nextCatalog);

      setSelectedThemeId((current) => {
        if (
          keepSelection &&
          nextCatalog.themes.some((theme) => theme.id === current)
        ) {
          return current;
        }
        return nextCatalog.themes[0]?.id || "";
      });
    } catch (error) {
      if (error.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login", { replace: true });
        return;
      }
      if (error.status === 403 && error.code === "THEME_MANAGEMENT_FORBIDDEN") {
        navigate("/unauthorized", { replace: true });
        return;
      }
      setMessage({
        type: "error",
        text:
          error.message ||
          tr("loadError", "Impossible de charger le catalogue."),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog({ keepSelection: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleThemes = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase("fr");
    if (!query) return catalog.themes;

    return catalog.themes.filter((theme) => {
      const searchable = [
        theme.name,
        ...(theme.subThemes || []).map((item) => item.name),
      ]
        .join(" ")
        .toLocaleLowerCase("fr");
      return searchable.includes(query);
    });
  }, [catalog.themes, searchTerm]);

  const selectedTheme = useMemo(
    () => catalog.themes.find((theme) => theme.id === selectedThemeId) || null,
    [catalog.themes, selectedThemeId]
  );

  useEffect(() => {
    setThemeName(selectedTheme?.name || "");
    setRenamingTheme(false);
    setSubThemePanelOpen(false);
  }, [selectedThemeId, selectedTheme?.name]);

  const applyCatalogResponse = (data, preferredThemeId = selectedThemeId) => {
    if (!data?.catalog) return;
    const nextThemes = Array.isArray(data.catalog.themes)
      ? data.catalog.themes
      : [];

    setCatalog({ ...data.catalog, themes: nextThemes });
    setSelectedThemeId(
      nextThemes.some((theme) => theme.id === preferredThemeId)
        ? preferredThemeId
        : nextThemes[0]?.id || ""
    );
  };

  const handleAddTheme = async (event) => {
    event.preventDefault();
    const name = newThemeName.trim();
    if (!name) return;

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const data = await request("/api/certifs/theme-catalog/themes", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      applyCatalogResponse(data);
      const createdTheme = data.catalog?.themes?.find(
        (theme) =>
          theme.name.toLocaleLowerCase("fr") === name.toLocaleLowerCase("fr")
      );
      if (createdTheme) setSelectedThemeId(createdTheme.id);
      setNewThemeName("");
      setMessage({
        type: "success",
        text: tr("themeCreated", "Thème ajouté au catalogue global."),
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRenameTheme = async () => {
    if (!selectedTheme || !themeName.trim()) return;

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const data = await request("/api/certifs/theme-catalog/themes", {
        method: "PUT",
        body: JSON.stringify({
          themeId: selectedTheme.id,
          name: themeName.trim(),
        }),
      });
      applyCatalogResponse(data, selectedTheme.id);
      setRenamingTheme(false);
      setMessage({
        type: "success",
        text: tr("themeUpdated", "Thème modifié pour tous les clients."),
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const openDeleteThemeDialog = () => {
    if (!selectedTheme) return;
    setDeleteSecretCode("");
    setDeleteDialog({
      type: "theme",
      themeId: selectedTheme.id,
      name: selectedTheme.name,
    });
  };

  const openNewSubTheme = () => {
    setSubThemeForm({
      subThemeId: "",
      name: "",
      duration: "1",
      competenciesFrText: "",
      competenciesEnText: "",
    });
    setSubThemePanelOpen(true);
  };

  const openEditSubTheme = (subTheme) => {
    const competencies = Array.isArray(subTheme.competencies)
      ? subTheme.competencies
      : (subTheme.prerequisites || []).map((text) => ({ fr: text, en: text }));

    setSubThemeForm({
      subThemeId: subTheme.id,
      name: subTheme.name,
      duration: String(subTheme.duration || 1),
      competenciesFrText: competencies.map((item) => item.fr || "").join("\n"),
      competenciesEnText: competencies.map((item) => item.en || item.fr || "").join("\n"),
    });
    setSubThemePanelOpen(true);
  };

  const handleSaveSubTheme = async (event) => {
    event.preventDefault();
    if (!selectedTheme) return;

    const frenchCompetencies = splitLines(subThemeForm.competenciesFrText);
    const englishCompetencies = splitLines(subThemeForm.competenciesEnText);

    if (frenchCompetencies.length !== englishCompetencies.length) {
      setMessage({
        type: "error",
        text: tr(
          "competencyTranslationsMismatch",
          "Chaque compétence française doit avoir une version anglaise sur la même ligne."
        ),
      });
      return;
    }

    const competencies = frenchCompetencies.map((fr, index) => ({
      fr,
      en: englishCompetencies[index],
    }));

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const data = await request("/api/certifs/theme-catalog/subthemes", {
        method: "PUT",
        body: JSON.stringify({
          themeId: selectedTheme.id,
          subThemeId: subThemeForm.subThemeId || undefined,
          name: subThemeForm.name.trim(),
          duration: Number(subThemeForm.duration),
          competencies,
        }),
      });
      applyCatalogResponse(data, selectedTheme.id);
      setSubThemePanelOpen(false);
      setMessage({
        type: "success",
        text: subThemeForm.subThemeId
          ? tr(
              "subThemeUpdated",
              "Sous-thème et compétences bilingues modifiés pour tous les clients."
            )
          : tr(
              "subThemeCreated",
              "Sous-thème ajouté au catalogue global."
            ),
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const openDeleteSubThemeDialog = (subTheme) => {
    if (!selectedTheme || !subTheme) return;
    setDeleteSecretCode("");
    setDeleteDialog({
      type: "subTheme",
      themeId: selectedTheme.id,
      subThemeId: subTheme.id,
      name: subTheme.name,
    });
  };

  const openDeleteCompetenceDialog = (subTheme, competence) => {
    if (!selectedTheme || !subTheme || !competence?.id) return;
    setDeleteSecretCode("");
    setDeleteDialog({
      type: "competence",
      themeId: selectedTheme.id,
      subThemeId: subTheme.id,
      competenceId: competence.id,
      name: competence?.[activeLanguage] || competence?.fr || competence?.en || "",
    });
  };

  const closeDeleteDialog = () => {
    if (deletingKey) return;
    setDeleteDialog(null);
    setDeleteSecretCode("");
  };

  const handleConfirmDelete = async (event) => {
    event.preventDefault();
    if (!deleteDialog || !deleteSecretCode.trim()) return;

    const isTheme = deleteDialog.type === "theme";
    const isCompetence = deleteDialog.type === "competence";
    const key = isTheme
      ? `theme-${deleteDialog.themeId}`
      : isCompetence
        ? `competence-${deleteDialog.competenceId}`
        : `subtheme-${deleteDialog.subThemeId}`;
    const path = isTheme
      ? `/api/certifs/theme-catalog/themes/${deleteDialog.themeId}`
      : isCompetence
        ? `/api/certifs/theme-catalog/competencies/${deleteDialog.themeId}/${deleteDialog.subThemeId}/${deleteDialog.competenceId}`
        : `/api/certifs/theme-catalog/subthemes/${deleteDialog.themeId}/${deleteDialog.subThemeId}`;

    setDeletingKey(key);
    setMessage({ type: "", text: "" });

    try {
      const data = await request(path, {
        method: "DELETE",
        body: JSON.stringify({ secretCode: deleteSecretCode }),
      });

      applyCatalogResponse(data, isTheme ? "" : deleteDialog.themeId);

      if (isTheme) {
        setSubThemePanelOpen(false);
        setMessage({
          type: "success",
          text: tr(
            "themeDeleted",
            "Le thème et tous ses sous-thèmes ont été supprimés du catalogue de l’organisation."
          ),
        });
      } else if (isCompetence) {
        setMessage({
          type: "success",
          text: tr(
            "competencyDeleted",
            "La compétence a été supprimée du sous-thème."
          ),
        });
      } else {
        if (subThemeForm.subThemeId === deleteDialog.subThemeId) {
          setSubThemePanelOpen(false);
        }
        setMessage({
          type: "success",
          text: tr(
            "subThemeDeleted",
            "Le sous-thème et ses compétences ont été supprimés du catalogue de l’organisation."
          ),
        });
      }

      setDeleteDialog(null);
      setDeleteSecretCode("");
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setDeletingKey("");
    }
  };

  const getDisplayedCompetencies = (subTheme) => {
    const competencies = Array.isArray(subTheme?.competencies)
      ? subTheme.competencies
      : (subTheme?.prerequisites || []).map((text, index) => ({
          id: `legacy-${index}`,
          fr: text,
          en: text,
        }));

    return competencies
      .map((item) => ({
        ...item,
        text: item?.[activeLanguage] || item?.fr || item?.en,
      }))
      .filter((item) => Boolean(item.text));
  };

  if (loading) {
    return (
      <div className="flex min-h-[65vh] items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          {tr("loading", "Chargement du catalogue…")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-3 pb-10 pt-20 sm:px-5">
      <div className="mx-auto max-w-7xl">
        <header className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-5 py-7 text-white shadow-xl shadow-slate-200/70 sm:px-8">
          <div className="absolute -right-14 -top-16 h-48 w-48 rounded-full bg-cyan-400/15 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                <BookOpenCheck className="h-6 w-6 text-cyan-300" />
              </span>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-300">
                  {tr("badge", "Catalogue partagé")}
                </p>
                <h1 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl">
                  {tr("title", "Gestion des thèmes et compétences")}
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
                  {tr(
                    "subtitle",
                    "Ajoutez, modifiez ou supprimez les thèmes, sous-thèmes, durées et compétences bilingues utilisés lors de la création des certificats."
                  )}
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-cyan-300/20 bg-cyan-300/10 px-4 py-3 text-sm text-cyan-100">
              <div className="flex items-center gap-2 font-bold">
                <Sparkles className="h-4 w-4" />
                {tr("globalNoticeTitle", "Modifications globales")}
              </div>
              <p className="mt-1 text-xs leading-5 text-cyan-100/80">
                {tr(
                  "globalNotice",
                  "Chaque changement est immédiatement visible pour tous les clients."
                )}
              </p>
            </div>
          </div>
        </header>

        {message.text ? (
          <div
            className={`mt-4 rounded-2xl border px-4 py-3 text-sm font-semibold ${
              message.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-rose-200 bg-rose-50 text-rose-700"
            }`}
          >
            {message.text}
          </div>
        ) : null}

        <div className="mt-5 grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
          <aside className="rounded-3xl border border-slate-200 bg-white p-4 shadow-lg shadow-slate-200/50">
            <form
              onSubmit={handleAddTheme}
              className="rounded-2xl bg-slate-950 p-4 text-white"
            >
              <div className="flex items-center gap-2 text-sm font-black">
                <FolderPlus className="h-4 w-4 text-cyan-300" />
                {tr("addTheme", "Ajouter un thème")}
              </div>
              <div className="mt-3 flex gap-2">
                <input
                  value={newThemeName}
                  onChange={(event) => setNewThemeName(event.target.value)}
                  placeholder={tr(
                    "themeNamePlaceholder",
                    "Nom du nouveau thème"
                  )}
                  className="h-11 min-w-0 flex-1 rounded-xl border border-white/10 bg-white/10 px-3 text-sm text-white outline-none placeholder:text-slate-400 focus:border-cyan-300"
                />
                <button
                  type="submit"
                  disabled={saving || !newThemeName.trim()}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-500 text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-50"
                  title={tr("add", "Ajouter")}
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </form>

            <div className="relative mt-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={tr(
                  "searchPlaceholder",
                  "Rechercher un thème ou sous-thème…"
                )}
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/60"
              />
            </div>

            <div className="mt-3 max-h-[60vh] space-y-2 overflow-y-auto pr-1">
              {visibleThemes.map((theme) => (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => setSelectedThemeId(theme.id)}
                  className={`flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                    selectedThemeId === theme.id
                      ? "border-blue-200 bg-blue-50 text-blue-900 shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{theme.name}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {(theme.subThemes || []).length}{" "}
                      {tr("subThemesCount", "sous-thème(s)")}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0" />
                </button>
              ))}

              {visibleThemes.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
                  {tr("noTheme", "Aucun thème trouvé.")}
                </div>
              ) : null}
            </div>
          </aside>

          <main className="min-w-0">
            {selectedTheme ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-lg shadow-slate-200/50 sm:p-6">
                <div className="flex flex-col gap-4 border-b border-slate-100 pb-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1">
                    {renamingTheme ? (
                      <div className="flex max-w-2xl gap-2">
                        <input
                          value={themeName}
                          onChange={(event) => setThemeName(event.target.value)}
                          className="h-11 min-w-0 flex-1 rounded-xl border border-blue-300 px-3 text-base font-bold text-slate-900 outline-none ring-4 ring-blue-100/60"
                        />
                        <button
                          type="button"
                          onClick={handleRenameTheme}
                          disabled={saving || !themeName.trim()}
                          className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white disabled:opacity-50"
                        >
                          <Check className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setThemeName(selectedTheme.name);
                            setRenamingTheme(false);
                          }}
                          className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-600"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
                          <Layers3 className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                          <h2 className="truncate text-xl font-black text-slate-950 sm:text-2xl">
                            {selectedTheme.name}
                          </h2>
                          <p className="mt-1 text-sm text-slate-500">
                            {(selectedTheme.subThemes || []).length}{" "}
                            {tr("subThemesCount", "sous-thème(s)")}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setRenamingTheme(true)}
                          title={tr(
                            "renameTheme",
                            "Modifier le nom du thème"
                          )}
                          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={openDeleteThemeDialog}
                          disabled={
                            saving ||
                            deletingKey === `theme-${selectedTheme.id}`
                          }
                          title={tr("deleteTheme", "Supprimer le thème")}
                          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-rose-200 text-rose-600 transition hover:bg-rose-50 disabled:opacity-50"
                        >
                          {deletingKey === `theme-${selectedTheme.id}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={openNewSubTheme}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-600/20"
                  >
                    <Plus className="h-4 w-4" />
                    {tr("addSubTheme", "Ajouter un sous-thème")}
                  </button>
                </div>

                <div className="mt-5 grid gap-3">
                  {(selectedTheme.subThemes || []).map((subTheme) => {
                    const displayedCompetencies = getDisplayedCompetencies(subTheme);

                    return (
                      <article
                        key={subTheme.id}
                        className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 transition hover:border-blue-200 hover:bg-blue-50/40"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-bold text-slate-900">
                              {subTheme.name}
                            </h3>
                            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 font-semibold ring-1 ring-slate-200">
                                <Clock3 className="h-3.5 w-3.5 text-blue-500" />
                                {subTheme.duration} {tr("days", "jour(s)")}
                              </span>
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 font-semibold ring-1 ring-slate-200">
                                <GraduationCap className="h-3.5 w-3.5 text-cyan-600" />
                                {displayedCompetencies.length}{" "}
                                {tr("competencies", "compétence(s)")}
                              </span>
                            </div>
                            {displayedCompetencies.length > 0 ? (
                              <ul className="mt-3 grid max-h-44 gap-1.5 overflow-y-auto pr-1 text-sm text-slate-600 sm:grid-cols-2">
                                {displayedCompetencies.map((item, index) => (
                                    <li
                                      key={item.id || `${subTheme.id}-${index}`}
                                      className="group flex items-start gap-2 rounded-lg px-1 py-0.5"
                                    >
                                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500" />
                                      <span className="line-clamp-2 min-w-0 flex-1">{item.text}</span>
                                      {item.id && !String(item.id).startsWith("legacy-") ? (
                                        <button
                                          type="button"
                                          onClick={() => openDeleteCompetenceDialog(subTheme, item)}
                                          title={tr("deleteCompetency", "Supprimer la compétence")}
                                          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-rose-500 opacity-0 transition hover:bg-rose-50 group-hover:opacity-100 focus:opacity-100"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      ) : null}
                                    </li>
                                  ))}
                              </ul>
                            ) : (
                              <p className="mt-3 text-sm italic text-slate-400">
                                {tr(
                                  "noCompetency",
                                  "Aucune compétence renseignée."
                                )}
                              </p>
                            )}
                          </div>

                          <div className="flex shrink-0 gap-2">
                            <button
                              type="button"
                              onClick={() => openEditSubTheme(subTheme)}
                              className="inline-flex h-10 items-center gap-2 rounded-xl border border-blue-200 bg-white px-3 text-xs font-bold text-blue-700 transition hover:bg-blue-50"
                            >
                              <Edit3 className="h-4 w-4" />
                              <span className="hidden sm:inline">
                                {tr("edit", "Modifier")}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => openDeleteSubThemeDialog(subTheme)}
                              disabled={
                                saving ||
                                deletingKey === `subtheme-${subTheme.id}`
                              }
                              title={tr(
                                "deleteSubTheme",
                                "Supprimer le sous-thème"
                              )}
                              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50 disabled:opacity-50"
                            >
                              {deletingKey === `subtheme-${subTheme.id}` ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}

                  {(selectedTheme.subThemes || []).length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-14 text-center">
                      <GraduationCap className="mx-auto h-8 w-8 text-blue-500" />
                      <h3 className="mt-3 font-bold text-slate-900">
                        {tr(
                          "emptyTheme",
                          "Ce thème ne contient encore aucun sous-thème."
                        )}
                      </h3>
                      <button
                        type="button"
                        onClick={openNewSubTheme}
                        className="mt-4 text-sm font-bold text-blue-700 hover:underline"
                      >
                        {tr(
                          "createFirstSubTheme",
                          "Créer le premier sous-thème"
                        )}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-20 text-center">
                <AlertTriangle className="mx-auto h-9 w-9 text-amber-500" />
                <p className="mt-3 font-bold text-slate-900">
                  {tr(
                    "selectThemeHint",
                    "Sélectionnez ou ajoutez un thème."
                  )}
                </p>
              </div>
            )}
          </main>
        </div>
      </div>

      {deleteDialog ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <button
            type="button"
            aria-label={tr("close", "Fermer")}
            onClick={closeDeleteDialog}
            className="absolute inset-0 cursor-default"
          />
          <form
            onSubmit={handleConfirmDelete}
            className="relative z-10 w-full max-w-md rounded-3xl border border-rose-100 bg-white p-6 shadow-2xl sm:p-7"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                <KeyRound className="h-6 w-6" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-rose-600">
                  {tr("protectedDeletion", "Suppression protégée")}
                </p>
                <h2 className="mt-2 text-xl font-black text-slate-950">
                  {deleteDialog.type === "theme"
                    ? tr("deleteThemeTitle", "Supprimer ce thème")
                    : deleteDialog.type === "competence"
                      ? tr("deleteCompetencyTitle", "Supprimer cette compétence")
                      : tr("deleteSubThemeTitle", "Supprimer ce sous-thème")}
                </h2>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-800">
              {deleteDialog.type === "theme"
                ? tr(
                    "confirmDeleteThemeWithCode",
                    "Le thème « {name} » et tous ses sous-thèmes seront supprimés. Saisissez le code de suppression de l’administrateur pour confirmer."
                  ).replace("{name}", deleteDialog.name)
                : deleteDialog.type === "competence"
                  ? tr(
                      "confirmDeleteCompetencyWithCode",
                      "La compétence « {name} » sera supprimée. Saisissez le code de suppression de l’administrateur pour confirmer."
                    ).replace("{name}", deleteDialog.name)
                  : tr(
                      "confirmDeleteSubThemeWithCode",
                      "Le sous-thème « {name} » et ses compétences seront supprimés. Saisissez le code de suppression de l’administrateur pour confirmer."
                    ).replace("{name}", deleteDialog.name)}
            </div>

            <label className="mt-5 block">
              <span className="text-sm font-bold text-slate-700">
                {tr("deleteSecretCodeLabel", "Code de suppression de l’administrateur")}
              </span>
              <input
                autoFocus
                type="password"
                autoComplete="off"
                value={deleteSecretCode}
                onChange={(event) => setDeleteSecretCode(event.target.value)}
                placeholder={tr("deleteSecretCodePlaceholder", "Saisissez le code de suppression")}
                className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100/70"
              />
            </label>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeDeleteDialog}
                disabled={Boolean(deletingKey)}
                className="h-11 rounded-xl border border-slate-200 px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                {tr("cancel", "Annuler")}
              </button>
              <button
                type="submit"
                disabled={Boolean(deletingKey) || !deleteSecretCode.trim()}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-rose-600 px-5 text-sm font-bold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deletingKey ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {deletingKey
                  ? tr("deleting", "Suppression…")
                  : tr("confirmDelete", "Confirmer la suppression")}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {subThemePanelOpen && selectedTheme ? (
        <div className="fixed inset-0 z-[90] flex items-end justify-end bg-slate-950/60 p-0 sm:p-4">
          <button
            type="button"
            aria-label={tr("close", "Fermer")}
            onClick={() => setSubThemePanelOpen(false)}
            className="absolute inset-0 cursor-default"
          />
          <form
            onSubmit={handleSaveSubTheme}
            className="relative z-10 h-full w-full overflow-y-auto bg-white p-5 shadow-2xl sm:h-auto sm:max-h-[92vh] sm:max-w-2xl sm:rounded-3xl sm:p-7"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                  {selectedTheme.name}
                </p>
                <h2 className="mt-2 text-2xl font-black text-slate-950">
                  {subThemeForm.subThemeId
                    ? tr("editSubTheme", "Modifier le sous-thème")
                    : tr("newSubTheme", "Nouveau sous-thème")}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setSubThemePanelOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 space-y-5">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  {tr("subThemeName", "Nom du sous-thème")}
                </span>
                <input
                  required
                  value={subThemeForm.name}
                  onChange={(event) =>
                    setSubThemeForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100/60"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  {tr("duration", "Durée par défaut (jours)")}
                </span>
                <input
                  required
                  type="number"
                  min="1"
                  max="365"
                  value={subThemeForm.duration}
                  onChange={(event) =>
                    setSubThemeForm((current) => ({
                      ...current,
                      duration: event.target.value,
                    }))
                  }
                  className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100/60"
                />
              </label>

              <div>
                <span className="text-sm font-bold text-slate-700">
                  {tr("competenciesLabel", "Compétences acquises")}
                </span>
                <span className="mt-1 block text-xs leading-5 text-slate-500">
                  {tr(
                    "competenciesBilingualHint",
                    "Saisissez les versions française et anglaise dans le même ordre, une compétence par ligne."
                  )}
                </span>

                <div className="mt-3 grid gap-4 md:grid-cols-2">
                  <label className="block rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <span className="text-xs font-black uppercase tracking-[0.14em] text-blue-700">
                      {tr("competenciesFrench", "Français")}
                    </span>
                    <textarea
                      rows={11}
                      value={subThemeForm.competenciesFrText}
                      onChange={(event) =>
                        setSubThemeForm((current) => ({
                          ...current,
                          competenciesFrText: event.target.value,
                        }))
                      }
                      placeholder={tr(
                        "competenciesFrenchPlaceholder",
                        "Comprendre les exigences…\nPlanifier un audit…\nIdentifier les écarts…"
                      )}
                      className="mt-2 w-full resize-y rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100/60"
                    />
                  </label>

                  <label className="block rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <span className="text-xs font-black uppercase tracking-[0.14em] text-cyan-700">
                      {tr("competenciesEnglish", "Anglais")}
                    </span>
                    <textarea
                      rows={11}
                      value={subThemeForm.competenciesEnText}
                      onChange={(event) =>
                        setSubThemeForm((current) => ({
                          ...current,
                          competenciesEnText: event.target.value,
                        }))
                      }
                      placeholder={tr(
                        "competenciesEnglishPlaceholder",
                        "Understand the requirements…\nPlan an audit…\nIdentify gaps…"
                      )}
                      className="mt-2 w-full resize-y rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 outline-none transition focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100/60"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setSubThemePanelOpen(false)}
                className="h-11 rounded-xl border border-slate-200 px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
              >
                {tr("cancel", "Annuler")}
              </button>
              <button
                type="submit"
                disabled={
                  saving ||
                  !subThemeForm.name.trim() ||
                  Number(subThemeForm.duration) < 1
                }
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-5 text-sm font-bold text-white shadow-lg shadow-blue-600/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saving
                  ? tr("saving", "Enregistrement…")
                  : tr("save", "Enregistrer")}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default ManageThemes;
